import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createAppColumnHelper, useClientTable } from "../../../components/table/tableFeatures";
import NiceModal from "@ebay/nice-modal-react";
import { css } from "../../../../styled-system/css";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { PageContainer } from "../../../components/core/basic/PageContainer";
import Card from "../../../components/core/basic/Card";
import { Button } from "../../../components/core/basic/Button";
import { Table } from "../../../components/table/Table";
import Loading from "../../../components/core/blocks/Loading";
import type { Upload } from "../../../zodios/apiDefinitions/uploads";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { useConfirmUpload } from "../../../modules/uploads/api/useConfirmUpload";
import { useUserUploads } from "../../../modules/uploads/api/useUserUploads";
import { isTerminalStatus, UploadStatus } from "../../../modules/uploads/constants";
import { StatusBadge } from "../../../modules/uploads/StatusBadge";
import { FaCircleCheck, FaCircleXmark } from "react-icons/fa6";
import { flattenUploadAlerts } from "../../../modules/uploads/alertUtils";
import { AlertSection } from "../../../modules/uploads/AlertSection";
import { IgnoreWarningsConfirmation } from "../../../modules/uploads/IgnoreWarningsConfirmation";
import { FaChevronDown } from "react-icons/fa6";
import { Button as AriaButton, Disclosure, DisclosurePanel } from "react-aria-components";
import { CampaignBulksheetUploadModal } from "../../../modules/advertising/CampaignBulksheetUploadModal";
import { PricingPlanBulkUploadModalV2 } from "../../../modules/pricingv2/PricingPlanBulkUploadV2";
import { PriceLinkUploadModal } from "../../../modules/pricingv2/PriceLinkUpload";
import { useDateFormatter } from "../../../i18n/useDateFormatter";

const disclosureTriggerStyles = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "0.375rem",
  background: "none",
  border: "none",
  padding: "0",
  cursor: "pointer",
  fontSize: "small",
  color: "gray.600",
  fontFamily: "inherit",
  _hover: { color: "gray.800" },
  "& > svg": { transition: "transform 200ms" },
  _expanded: { "& > svg": { transform: "rotate(180deg)" } },
});

const UPLOAD_TYPE_MODALS: Record<string, typeof CampaignBulksheetUploadModal> = {
  bulk_upload: CampaignBulksheetUploadModal,
  pricing_plan: PricingPlanBulkUploadModalV2,
  price_link_upload: PriceLinkUploadModal,
};

function openUploadModal(upload: Upload) {
  const ModalComponent = UPLOAD_TYPE_MODALS[upload.upload_type];
  if (ModalComponent) {
    void NiceModal.show(ModalComponent, { uploadId: upload.id });
  }
}

const columnHelper = createAppColumnHelper<Upload>();

function UploadExpander({ upload }: { upload: Upload }) {
  const { t } = useTranslation(["uploads"]);
  const { confirmProcess, ignoreWarnings, isPending } = useConfirmUpload(upload.id);

  const uploadTypeConfirmationPhrase = (uploadType: string): string => {
    switch (uploadType) {
      case "bulk_upload":
        return t(($) => $.uploads["campaign bulksheet"]);
      case "pricing_plan":
        return t(($) => $.uploads["pricing plan"]);
      default:
        return t(($) => $.uploads.upload);
    }
  };

  const { errors, warnings } = flattenUploadAlerts(upload);
  const canIgnoreWarnings = upload.status === UploadStatus.VALIDATED && errors.length === 0 && warnings.length > 0;
  const canConfirmProcess = upload.status === UploadStatus.VALIDATED && errors.length === 0 && warnings.length === 0;

  return (
    <div>
      <VStack gap="small" alignItems="stretch">
        {upload.status === UploadStatus.SUCCESS && (
          <VStack gap="small" alignItems="center" padding="medium">
            <Flex gap="medium" alignItems="center">
              <FaCircleCheck className={css({ color: "trellisPalette.12", fontSize: 24 })} />
              <p className={css({ fontWeight: "semibold", margin: 0 })}>
                {t(($) => $.uploads["Successfully uploaded and processed."])}
              </p>
            </Flex>
          </VStack>
        )}
        {upload.status === UploadStatus.FAILED && (
          <VStack gap="small" alignItems="center" padding="medium">
            <Flex gap="medium" alignItems="center">
              <FaCircleXmark className={css({ color: "error", fontSize: 24 })} />
              <p className={css({ fontWeight: "semibold", margin: 0 })}>
                {t(($) => $.uploads["Processing failed due to a server error."])}
              </p>
            </Flex>
          </VStack>
        )}
        {upload.status === UploadStatus.VALIDATION_FAILED && (
          <VStack gap="small" alignItems="center" padding="medium">
            <Flex gap="medium" alignItems="center">
              <FaCircleXmark className={css({ color: "error", fontSize: 24 })} />
              <p className={css({ fontWeight: "semibold", margin: 0 })}>
                {t(($) => $.uploads["Validation failed. Fix the issues below and upload a corrected file."])}
              </p>
            </Flex>
          </VStack>
        )}
        {canConfirmProcess && (
          <Flex justifyContent="center" padding="medium">
            <Button onPress={() => confirmProcess()} isDisabled={isPending}>
              {t(($) => $.uploads.view["Confirm & Process"])}
            </Button>
          </Flex>
        )}
        {upload.status === UploadStatus.VALIDATED && errors.length > 0 && <AlertSection type="error" items={errors} />}
        {warnings.length > 0 && upload.status === UploadStatus.SUCCESS && (
          <Disclosure>
            <Flex justifyContent="center">
              <AriaButton slot="trigger" className={disclosureTriggerStyles}>
                <FaChevronDown className={css({ fontSize: 10 })} />
                {t(($) => $.uploads.view.warnings, { count: warnings.length })}
              </AriaButton>
            </Flex>
            <DisclosurePanel>
              <AlertSection type="warning" items={warnings} />
            </DisclosurePanel>
          </Disclosure>
        )}
        {upload.status === UploadStatus.VALIDATION_FAILED && (
          <>
            {errors.length > 0 && <AlertSection type="error" items={errors} />}
            {warnings.length > 0 && <AlertSection type="warning" items={warnings} />}
          </>
        )}
        {upload.status === UploadStatus.FAILED && (errors.length > 0 || warnings.length > 0) && (
          <Disclosure>
            <Flex justifyContent="center">
              <AriaButton slot="trigger" className={disclosureTriggerStyles}>
                <FaChevronDown className={css({ fontSize: 10 })} />
                <span>
                  {errors.length > 0 && warnings.length > 0 ?
                    t(($) => $.uploads.view["errors and warnings"], {
                      errorCount: errors.length,
                      warningCount: warnings.length,
                    })
                  : errors.length > 0 ?
                    t(($) => $.uploads["errors"], { count: errors.length })
                  : t(($) => $.uploads.view.warnings, { count: warnings.length })}
                </span>
              </AriaButton>
            </Flex>
            <DisclosurePanel>
              {errors.length > 0 && <AlertSection type="error" items={errors} />}
              {warnings.length > 0 && <AlertSection type="warning" items={warnings} />}
            </DisclosurePanel>
          </Disclosure>
        )}
        {warnings.length > 0 && upload.status === UploadStatus.VALIDATED && (
          <AlertSection type="warning" items={warnings}>
            {canIgnoreWarnings && (
              <IgnoreWarningsConfirmation
                confirmationPhrase={uploadTypeConfirmationPhrase(upload.upload_type)}
                onConfirm={() => ignoreWarnings()}
                isDisabled={isPending}
              />
            )}
          </AlertSection>
        )}
      </VStack>
    </div>
  );
}

function hasAlerts(upload: Upload): boolean {
  if (upload.status === UploadStatus.FAILED || upload.status === UploadStatus.VALIDATION_FAILED) return true;
  const { errors, warnings } = flattenUploadAlerts(upload);
  return errors.length > 0 || warnings.length > 0;
}

function UploadsPage() {
  const { t } = useTranslation(["uploads"]);
  const fmt = useDateFormatter("shortDateTime");
  const hasBulkUploadModule = useModuleEnabled("BULK_CAMPAIGN_UPLOAD");
  const { data: uploads, isLoading, isFetching } = useUserUploads();
  const hasActiveUploads = uploads?.some((u) => !isTerminalStatus(u.status));
  const hasDebugModule = useModuleEnabled("DEBUG");

  const uploadTypeLabel = useCallback(
    (uploadType: string): string | undefined => {
      switch (uploadType) {
        case "bulk_upload":
          return t(($) => $.uploads["Campaign Bulksheet"]);
        case "pricing_plan":
          return t(($) => $.uploads["Pricing Plan"]);
        default:
          return undefined;
      }
    },
    [t],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t(($) => $.uploads.columns["File Name"]),
        size: 460,
        minSize: 200,
        enableSorting: false,
        cell: (info) => (
          <Button
            variant="link"
            onPress={() => openUploadModal(info.row.original)}
            className={css({
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            })}
          >
            {info.getValue()}
          </Button>
        ),
      }),
      columnHelper.accessor("upload_type", {
        header: t(($) => $.uploads.columns.Type),
        cell: (info) => uploadTypeLabel(info.getValue()) ?? info.getValue(),
        size: 140,
        minSize: 100,
        maxSize: 200,
        enableResizing: false,
        enableSorting: false,
      }),
      columnHelper.accessor("status", {
        header: t(($) => $.uploads.columns.Status),
        enableSorting: true,
        cell: (info) => {
          const status = info.getValue();
          const progress = info.row.original.progress;
          const showProgress = !isTerminalStatus(status) && progress > 0;
          return (
            <HStack gap="small" alignItems="center">
              <StatusBadge status={status} />
              {showProgress && (
                <span className={css({ fontSize: "small", color: "gray.600" })}>{Math.round(progress)}%</span>
              )}
            </HStack>
          );
        },
        size: 180,
        minSize: 120,
        maxSize: 250,
        enableResizing: false,
      }),
      columnHelper.accessor("created_on", {
        header: t(($) => $.uploads.columns.Created),
        enableSorting: true,
        cell: (info) => fmt.format(new Date(info.getValue())),
        size: 220,
        minSize: 160,
        enableResizing: false,
      }),
    ],
    [t, uploadTypeLabel, fmt],
  );

  const table = useClientTable({
    data: uploads,
    columns,
    getRowCanExpand: (row) => hasAlerts(row.original) || row.original.status === UploadStatus.VALIDATED,
    getRowId: (row) => row.id.toString(),
    initialState: {
      sorting: [{ id: "created_on", desc: true }],
    },
  });

  return (
    <PageContainer>
      <VStack
        gap="medium"
        alignItems="stretch"
        alignSelf="center"
        width="100%"
        maxWidth="1200px"
        paddingY="large"
        paddingX={{ base: "medium", md: "xxlarge" }}
      >
        <HStack justifyContent="space-between" alignItems="center">
          <h1 className={css({ fontSize: "xxlarge", fontWeight: "bold" })}>{t(($) => $.uploads.Uploads)}</h1>
          <HStack gap="medium" alignItems="center">
            {hasActiveUploads && (
              <span className={css({ fontSize: "small", color: "gray.500" })}>
                {isFetching ? t(($) => $.uploads["Refreshing..."]) : t(($) => $.uploads["Auto-refresh active"])}
              </span>
            )}
            {hasBulkUploadModule && (
              <Button onPress={() => void NiceModal.show(CampaignBulksheetUploadModal)}>
                {t(($) => $.uploads["Campaign Bulksheet Upload"])}
              </Button>
            )}
            {hasDebugModule && (
              <Button onPress={() => void NiceModal.show(PricingPlanBulkUploadModalV2)}>
                {t(($) => $.uploads["Bulk Upload Pricing Plans"])}
              </Button>
            )}
          </HStack>
        </HStack>

        <Card>
          {isLoading ?
            <Loading fullPage={false} height="200px" backgroundColor="inherit" />
          : !uploads || uploads.length === 0 ?
            <Flex justifyContent="center" alignItems="center" padding="xxlarge">
              <p className={css({ color: "gray.500", fontSize: "medium" })}>
                {t(($) => $.uploads["No uploads yet. Use the buttons above to start your first upload."])}
              </p>
            </Flex>
          : <Table table={table} renderExpander={(row) => <UploadExpander upload={row.original} />} />}
        </Card>
      </VStack>
    </PageContainer>
  );
}

export const Route = createFileRoute("/_authenticated/user/uploads")({
  component: UploadsPage,
  staticData: { application: "Admin" },
});
